import {SocialPlatform} from '@Components/social-media/social-media.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {isFacebookAccount, isInstagramAccount, isLinkedinAccount, isThreadsAccount, isTikTokAccount, isTwitterAccount, isYoutubeAccount} from '@Components/social-media/account.vo';
import {GoogleBusinessProfileCtaType, PlatformMediaType} from '@Components/social-media/post.vo';

export enum SocialPostContentType {
  DEFAULT = 'DEFAULT', // default is in the dialog default
  MEDIA_RESIZING = 'MEDIA_RESIZING', // resizing
  MEDIA_RESIZING_FAILURE = 'MEDIA_RESIZE_FAILURE', // failure
  ORPHANED_PREVIEW = 'ORPHANED_PREVIEW', // accounts page
  ACCOUNT_PREVIEW = 'ACCOUNT_PREVIEW', // this is when u click an account in the dialog
}

export interface ReelDimensions {
  width: string;
  height: string;
}

export interface PostDimensions {
  width: string;
  height: string;
}

export interface ContentBackgroundConfig {
  width: string;
  height: string;
  scale: string;
}

export const PostDimensionsConfig: {[key in SocialPlatform]: PostDimensions} = {
  [SocialPlatform.FACEBOOK]: {width: '484px', height: '570px'},
  [SocialPlatform.FACEBOOK_GROUP]: {width: '484px', height: '570px'},
  [SocialPlatform.FACEBOOK_PAGE]: {width: '484px', height: '570px'},
  [SocialPlatform.FACEBOOK_PROFILE]: {width: '484px', height: '570px'},
  [SocialPlatform.TWITTER]: {width: '500px', height: '570px'},
  [SocialPlatform.TIKTOK]: {width: '484px', height: '570px'},
  [SocialPlatform.INSTAGRAM]: {width: '484px', height: '570px'},
  [SocialPlatform.LINKEDIN]: {width: '500px', height: '570px'},
  [SocialPlatform.LINKEDIN_PAGE]: {width: '500px', height: '570px'},
  [SocialPlatform.LINKEDIN_PROFILE]: {width: '500px', height: '570px'},
  [SocialPlatform.YOUTUBE]: {width: '484px', height: '570px'},
  [SocialPlatform.PINTEREST]: {width: '484px', height: '570px'},
  [SocialPlatform.GOOGLE_BUSINESS_PROFILE]: {width: '500px', height: '570px'},
  [SocialPlatform.THREADS]: {width: '500px', height: '570px'},
};

export const StoryDimensionsForAllPlatforms: PostDimensions = {
  width: '408px',
  height: '581px',
};

export const ReelDimensionsConfig: {[key in SocialPlatform]: ReelDimensions} = {
  [SocialPlatform.FACEBOOK]: {width: '408px', height: '581px'},
  [SocialPlatform.FACEBOOK_GROUP]: {width: '408px', height: '581px'},
  [SocialPlatform.FACEBOOK_PAGE]: {width: '408px', height: '581px'},
  [SocialPlatform.FACEBOOK_PROFILE]: {width: '408px', height: '581px'},
  [SocialPlatform.TWITTER]: {width: '408px', height: '581px'},
  [SocialPlatform.TIKTOK]: {width: '408px', height: '581px'},
  [SocialPlatform.INSTAGRAM]: {width: '408px', height: '581px'},
  [SocialPlatform.LINKEDIN]: {width: '408px', height: '581px'},
  [SocialPlatform.LINKEDIN_PAGE]: {width: '408px', height: '581px'},
  [SocialPlatform.LINKEDIN_PROFILE]: {width: '408px', height: '581px'},
  [SocialPlatform.YOUTUBE]: {width: '500px', height: '581px'},
  [SocialPlatform.PINTEREST]: {width: '408px', height: '581px'},
  [SocialPlatform.GOOGLE_BUSINESS_PROFILE]: {width: '500px', height: '581px'},
  [SocialPlatform.THREADS]: {width: '408px', height: '581px'},
};

export const reelOptionsIcons: {[key: string]: string[]} = {
  facebook: [],
  youtube: ['icon-thumbs-up', 'icon-thumbs-down', 'icon-message', 'icon-share-external', 'icon-more-h'],
  tiktok: ['icon-heart', 'icon-message', 'icon-bookmark', 'icon-share-external'],
  instagram: ['icon-heart', 'icon-message', 'icon-send', 'icon-menu-dots'],
};

export const googleBusinessProfileCtaText: {[key in GoogleBusinessProfileCtaType]: string} = {
  [GoogleBusinessProfileCtaType.BOOK]: window.i18next.t('pmwjs_google_business_profile_cta_book'),
  [GoogleBusinessProfileCtaType.ORDER]: window.i18next.t('pmwjs_google_business_profile_cta_order'),
  [GoogleBusinessProfileCtaType.SHOP]: window.i18next.t('pmwjs_google_business_profile_cta_shop'),
  [GoogleBusinessProfileCtaType.LEARN_MORE]: window.i18next.t('pmwjs_google_business_profile_cta_learn'),
  [GoogleBusinessProfileCtaType.SIGN_UP]: window.i18next.t('pmwjs_google_business_profile_cta_signup'),
  [GoogleBusinessProfileCtaType.CALL]: window.i18next.t('pmwjs_google_business_profile_cta_call'),
};

export const getPreviewStyleConfig = (accountType: SocialPlatform, isReel: boolean, platformMediaType: PlatformMediaType): ReelDimensions | PostDimensions => {
  if (platformMediaType === PlatformMediaType.STORIES) {
    return StoryDimensionsForAllPlatforms;
  }

  return isReel ? ReelDimensionsConfig[accountType] : PostDimensionsConfig[accountType];
};

export function getSocialPlatformString(socialAccount: SocialAccount | undefined): string {
  if (isFacebookAccount(socialAccount)) {
    return 'facebook';
  }

  if (isTwitterAccount(socialAccount)) {
    return 'twitter';
  }

  if (isYoutubeAccount(socialAccount)) {
    return 'youtube';
  }

  if (isLinkedinAccount(socialAccount)) {
    return 'linkedin';
  }

  if (isInstagramAccount(socialAccount)) {
    return 'instagram';
  }

  if (isTikTokAccount(socialAccount)) {
    return 'tiktok';
  }

  if (isThreadsAccount(socialAccount)) {
    return 'threads';
  }

  return 'facebook';
}
